import { Box, Stack } from '@mui/material';
import { useCallback } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { TextInput } from '@/components/form/baseInputs/TextInput';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useForm } from '@/components/react-hook-form';

import {
  AdminConfigurationsPageDocument,
  useUpdateIntegrationConfigurationFromAdminMutation,
} from '../graphql/AdminConfigurationsPage.generated';

interface SnowflakeSectionProps {
  integrationConfigurationId: string | undefined;
  currentValues: {
    snowflakeDataSharingDatabase: string | null | undefined;
    snowflakeDataSharingRole: string | null | undefined;
    snowflakeIntegrationDisplayName: string | null | undefined;
  };
}

interface SnowflakeFormShape {
  snowflakeDataSharingDatabase: string;
  snowflakeDataSharingRole: string;
  snowflakeIntegrationDisplayName: string;
}

export function SnowflakeSection({
  integrationConfigurationId,
  currentValues,
}: SnowflakeSectionProps) {
  const { showFeedback, createErrorFeedback } = useFeedback();

  const formMethods = useForm<SnowflakeFormShape>({
    defaultValues: {
      snowflakeDataSharingDatabase:
        currentValues.snowflakeDataSharingDatabase ?? '',
      snowflakeDataSharingRole: currentValues.snowflakeDataSharingRole ?? '',
      snowflakeIntegrationDisplayName:
        currentValues.snowflakeIntegrationDisplayName ?? '',
    },
  });

  const [updateIntegrationConfiguration, { loading }] =
    useUpdateIntegrationConfigurationFromAdminMutation({
      refetchQueries: [{ query: AdminConfigurationsPageDocument }],
      awaitRefetchQueries: true,
      onError: createErrorFeedback('Failed to update Snowflake configuration'),
    });

  const hasPersistedSnowflakeConfiguration = Boolean(
    currentValues.snowflakeDataSharingDatabase?.trim() ||
      currentValues.snowflakeDataSharingRole?.trim() ||
      currentValues.snowflakeIntegrationDisplayName?.trim()
  );
  const canClearSnowflakeConfiguration = Boolean(
    integrationConfigurationId && hasPersistedSnowflakeConfiguration
  );

  const onSubmit = useCallback(
    (formValues: SnowflakeFormShape) => {
      if (!integrationConfigurationId) return;
      const displayName = formValues.snowflakeIntegrationDisplayName.trim();

      return updateIntegrationConfiguration({
        variables: {
          input: {
            id: integrationConfigurationId,
            update: {
              snowflakeDataSharingDatabase:
                formValues.snowflakeDataSharingDatabase,
              snowflakeDataSharingRole: formValues.snowflakeDataSharingRole,
              snowflakeIntegrationDisplayName: displayName || null,
              clearSnowflakeIntegrationDisplayName: !displayName,
            },
          },
        },
        onCompleted: () => {
          showFeedback('Snowflake configuration updated', {
            variant: 'success',
          });
        },
      });
    },
    [integrationConfigurationId, showFeedback, updateIntegrationConfiguration]
  );

  const onClearConfiguration = useCallback(async () => {
    if (!integrationConfigurationId || !canClearSnowflakeConfiguration) {
      return;
    }

    await updateIntegrationConfiguration({
      variables: {
        input: {
          id: integrationConfigurationId,
          update: {
            clearSnowflakeDataSharingDatabase: true,
            clearSnowflakeDataSharingRole: true,
            clearSnowflakeIntegrationDisplayName: true,
          },
        },
      },
      onCompleted: () => {
        formMethods.setValue('snowflakeDataSharingDatabase', '');
        formMethods.setValue('snowflakeDataSharingRole', '');
        formMethods.setValue('snowflakeIntegrationDisplayName', '');
        showFeedback('Snowflake configuration cleared', { variant: 'success' });
      },
    });
  }, [
    canClearSnowflakeConfiguration,
    formMethods,
    integrationConfigurationId,
    showFeedback,
    updateIntegrationConfiguration,
  ]);

  if (!integrationConfigurationId) {
    return null;
  }

  return (
    <Box component="form" onSubmit={formMethods.handleSubmit(onSubmit)}>
      <Stack spacing={2} maxWidth={400}>
        <Callout severity="info-high">
          Customer users must enable Snowflake in the integrations pane.
        </Callout>
        <TextInput
          label="Database"
          placeholder="SHARED_FROM_LUM_DEMO_SHARE_ACCOUNT"
          {...formMethods.register('snowflakeDataSharingDatabase', {
            required: 'Database is required',
          })}
          errorMessage={
            formMethods.formState.errors.snowflakeDataSharingDatabase?.message
          }
        />
        <TextInput
          label="Role"
          placeholder="STAGING_LUM_DEMO_SHARE_ACCOUNT_SHARE_ROLE"
          {...formMethods.register('snowflakeDataSharingRole', {
            required: 'Role is required',
          })}
          errorMessage={
            formMethods.formState.errors.snowflakeDataSharingRole?.message
          }
        />
        <TextInput
          label="Snowflake integration display name"
          {...formMethods.register('snowflakeIntegrationDisplayName')}
        />
        <Box>
          <Stack direction="row" spacing={1}>
            <Button loading={loading} variant="primary" type="submit" size="sm">
              Save Snowflake configuration
            </Button>
            <Button
              variant="destructive-secondary"
              type="button"
              size="sm"
              disabled={!canClearSnowflakeConfiguration || loading}
              onClick={onClearConfiguration}
            >
              Clear Snowflake configuration
            </Button>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}
